import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TablePagination,
    alpha
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Add as AddIcon
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { APIEndpointOut, APIEndpointCreate, APIEndpointUpdate } from 'src/api/Api';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';

interface ApiEndpointsProps {
    refreshTrigger?: number;
}

const ApiEndpoints: React.FC<ApiEndpointsProps> = ({ refreshTrigger }) => {
    const [endpoints, setEndpoints] = useState<APIEndpointOut[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { showSnackbar, snackbarType } = useSnackbar();

    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedEndpoint, setSelectedEndpoint] = useState<APIEndpointOut | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

    // Form states
    const [formData, setFormData] = useState<APIEndpointCreate>({
        service_name: '',
        path: '',
        method: 'GET',
        description: '',
        enabled: true
    });

    const fetchEndpoints = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await clientService.api.listApiEndpointsApiV1ApiEndpointsGet({
                page: page + 1,
                per_page: rowsPerPage,
                sort_by: 'created_at',
                sort_order: 'desc'
            });
            if (response.ok) {
                setEndpoints(response.data.endpoints);
                setTotalCount(response.data.count);
            }
        } catch (error) {
            console.error('Failed to fetch API endpoints:', error);
            showSnackbar({ message: 'Failed to fetch API endpoints', type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, showSnackbar, snackbarType]);

    useEffect(() => {
        fetchEndpoints();
    }, [fetchEndpoints, refreshTrigger]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCreateOpen = () => {
        setFormData({
            service_name: '',
            path: '',
            method: 'GET',
            description: '',
            enabled: true
        });
        setCreateDialogOpen(true);
    };

    const handleEditOpen = (endpoint: APIEndpointOut) => {
        setSelectedEndpoint(endpoint);
        setFormData({
            service_name: endpoint.service_name,
            path: endpoint.path,
            method: endpoint.method,
            description: endpoint.description || '',
            enabled: endpoint.enabled
        });
        setEditDialogOpen(true);
    };

    const handleDeleteOpen = (endpoint: APIEndpointOut) => {
        setSelectedEndpoint(endpoint);
        setDeleteDialogOpen(true);
    };

    const handleCreateSubmit = async () => {
        setActionLoading(true);
        try {
            const response = await clientService.api.createApiEndpointApiV1ApiEndpointsPost(formData);
            if (response.ok) {
                showSnackbar({ message: 'API Endpoint created successfully', type: snackbarType.SUCCESS });
                fetchEndpoints();
                setCreateDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to create API endpoint:', error);
            showSnackbar({ message: 'Failed to create API endpoint', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditSubmit = async () => {
        if (!selectedEndpoint) return;
        setActionLoading(true);
        try {
            const updateData: APIEndpointUpdate = {
                service_name: formData.service_name,
                path: formData.path,
                method: formData.method,
                description: formData.description,
                enabled: formData.enabled
            };
            const response = await clientService.api.updateApiEndpointApiV1ApiEndpointsEndpointIdPut(selectedEndpoint.id, updateData);
            if (response.ok) {
                showSnackbar({ message: 'API Endpoint updated successfully', type: snackbarType.SUCCESS });
                fetchEndpoints();
                setEditDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to update API endpoint:', error);
            showSnackbar({ message: 'Failed to update API endpoint', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteSubmit = async () => {
        if (!selectedEndpoint) return;
        setActionLoading(true);
        try {
            const response = await clientService.api.deleteApiEndpointApiV1ApiEndpointsEndpointIdDelete(selectedEndpoint.id);
            if (response.ok) {
                showSnackbar({ message: 'API Endpoint deleted successfully', type: snackbarType.SUCCESS });
                fetchEndpoints();
                setDeleteDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to delete API endpoint:', error);
            showSnackbar({ message: 'Failed to delete API endpoint', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggle = async (endpoint: APIEndpointOut) => {
        setTogglingIds(prev => new Set(prev).add(endpoint.id));
        try {
            const response = await clientService.api.toggleApiEndpointApiV1ApiEndpointsEndpointIdTogglePatch(endpoint.id, { enabled: !endpoint.enabled });
            if (response.ok) {
                showSnackbar({ message: `Endpoint ${!endpoint.enabled ? 'enabled' : 'disabled'} successfully`, type: snackbarType.SUCCESS });
                setEndpoints(prevEndpoints => 
                    prevEndpoints.map(ep => 
                        ep.id === endpoint.id ? { ...ep, enabled: !ep.enabled } : ep
                    )
                );
            }
        } catch (error) {
            console.error('Failed to toggle API endpoint:', error);
            showSnackbar({ message: 'Failed to toggle API endpoint', type: snackbarType.ERROR });
        } finally {
            setTogglingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(endpoint.id);
                return newSet;
            });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    startIcon={<AddIcon />} 
                    onClick={handleCreateOpen} 
                    variant="contained"
                    sx={{
                        bgcolor: '#10B981',
                        color: '#FFF',
                        fontWeight: 700,
                        fontSize: '14px',
                        px: 3,
                        py: 1,
                        borderRadius: '10px',
                        textTransform: 'none',
                        boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)',
                        '&:hover': {
                            bgcolor: '#059669',
                            boxShadow: '0 6px 20px rgba(5, 150, 105, 0.23)',
                        }
                    }}
                >
                    Add Endpoint
                </Button>
            </Box>

            <Box sx={{
                bgcolor: 'rgba(28, 28, 28, 0.4)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                overflow: 'hidden'
            }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 0.8fr 2fr 2fr 0.8fr 1.2fr 100px',
                    py: 1.5,
                    px: 3,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    bgcolor: 'rgba(255, 255, 255, 0.02)'
                }}>
                    {['SERVICE NAME', 'METHOD', 'PATH', 'DESCRIPTION', 'STATUS', 'CREATED AT', 'ACTIONS'].map((h, i) => (
                        <Typography key={h} variant="caption" sx={{ color: i === 0 ? '#10B981' : 'rgba(255, 255, 255, 0.3)', fontWeight: 700, letterSpacing: '1px' }}>
                            {h}
                        </Typography>
                    ))}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: '#10B981' }} />
                    </Box>
                ) : endpoints.length === 0 ? (
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', textAlign: 'center', py: 8 }}>
                        No API endpoints found
                    </Typography>
                ) : (
                    endpoints.map((endpoint) => (
                        <Box key={endpoint.id} sx={{
                            display: 'grid',
                            gridTemplateColumns: '1.5fr 0.8fr 2fr 2fr 0.8fr 1.2fr 100px',
                            alignItems: 'center',
                            py: 2,
                            px: 3,
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' }
                        }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>{endpoint.service_name}</Typography>
                            <Box>
                                <Chip 
                                    label={endpoint.method} 
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(
                                            endpoint.method === 'GET' ? '#42a5f5' :
                                            endpoint.method === 'POST' ? '#66bb6a' :
                                            endpoint.method === 'PUT' ? '#ffa726' :
                                            endpoint.method === 'DELETE' ? '#ef5350' : '#bdbdbd', 
                                            0.1
                                        ),
                                        color: 
                                            endpoint.method === 'GET' ? '#42a5f5' :
                                            endpoint.method === 'POST' ? '#66bb6a' :
                                            endpoint.method === 'PUT' ? '#ffa726' :
                                            endpoint.method === 'DELETE' ? '#ef5350' : '#bdbdbd',
                                        fontWeight: 700,
                                        fontSize: '10px',
                                        borderRadius: '4px'
                                    }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ color: '#00E676', fontFamily: 'monospace', fontSize: '12px' }}>{endpoint.path}</Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', pr: 2 }}>{endpoint.description || '-'}</Typography>
                            <Box>
                                <Switch
                                    checked={endpoint.enabled}
                                    onChange={() => handleToggle(endpoint)}
                                    color="success"
                                    size="small"
                                    disabled={togglingIds.has(endpoint.id)}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{formatDate(endpoint.created_at)}</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleEditOpen(endpoint)} 
                                        sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#42a5f5', bgcolor: alpha('#42a5f5', 0.1) } }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleDeleteOpen(endpoint)} 
                                        sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#ef5350', bgcolor: alpha('#ef5350', 0.1) } }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
                    Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount} endpoints
                </Typography>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        color: 'rgba(255,255,255,0.5)',
                        '.MuiTablePagination-selectIcon': { color: 'rgba(255,255,255,0.5)' },
                        '.MuiIconButton-root': { color: 'rgba(255,255,255,0.5)' },
                        border: 'none'
                    }}
                />
            </Box>

            {/* Create/Edit Dialog - Styled */}
            <Dialog 
                open={createDialogOpen || editDialogOpen} 
                onClose={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: '#121212',
                        color: '#fff',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>{createDialogOpen ? 'Add API Endpoint' : 'Edit API Endpoint'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                        <TextField
                            label="Service Name"
                            value={formData.service_name}
                            onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                            fullWidth
                            required
                            variant="outlined"
                            InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth required>
                                <InputLabel sx={{ color: 'rgba(255,255,255,0.5)' }}>Method</InputLabel>
                                <Select
                                    value={formData.method}
                                    label="Method"
                                    onChange={(e) => setFormData({ ...formData, method: e.target.value as any })}
                                    sx={{
                                        color: '#fff',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                                        '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' }
                                    }}
                                    MenuProps={{
                                        PaperProps: { sx: { bgcolor: '#1A1A1A', color: '#fff' } }
                                    }}
                                >
                                    <MenuItem value="GET">GET</MenuItem>
                                    <MenuItem value="POST">POST</MenuItem>
                                    <MenuItem value="PUT">PUT</MenuItem>
                                    <MenuItem value="DELETE">DELETE</MenuItem>
                                    <MenuItem value="PATCH">PATCH</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Path"
                                value={formData.path}
                                onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                                fullWidth
                                required
                                placeholder="/api/v1/..."
                                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                        '&.Mui-focused fieldset': { borderColor: '#10B981' },
                                    }
                                }}
                            />
                        </Box>
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={3}
                            InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                                }
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.enabled ?? false}
                                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                                    color="success"
                                />
                            }
                            label="Enabled"
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }} sx={{ color: 'rgba(255,255,255,0.5)' }}>Cancel</Button>
                    <Button 
                        onClick={createDialogOpen ? handleCreateSubmit : handleEditSubmit} 
                        variant="contained" 
                        disabled={actionLoading || !formData.service_name || !formData.path || !formData.method}
                        sx={{ bgcolor: '#10B981', color: '#fff', borderRadius: '8px', px: 4, '&:hover': { bgcolor: '#059669' } }}
                    >
                        {actionLoading ? <CircularProgress size={20} color="inherit" /> : (createDialogOpen ? 'Create' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog - Styled */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        bgcolor: '#121212',
                        color: '#fff',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Delete API Endpoint</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', py: 1 }}>
                        Are you sure you want to delete this endpoint? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteSubmit} 
                        variant="contained" 
                        disabled={actionLoading}
                        sx={{ bgcolor: '#FF5252', color: '#fff', borderRadius: '8px', px: 4, '&:hover': { bgcolor: '#D32F2F' } }}
                    >
                        {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};


export default ApiEndpoints;
